import { Alert, InputNumber, Modal, message, Select, Table } from "antd";
import React, {
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import AdminModalContext from "../context/AdminModalContext";
import { formatApiErrorDetail } from "../utils/apiErrors";
import useAxios from "../utils/useAxios";

// Issue #7: 稼働日数マスタ（日本/カイロ × 12ヶ月）の管理画面。
// 過去年度は編集不可（バックエンドの bulk_update も同条件で拒否する）
const CURRENT_YEAR = new Date().getFullYear();
const SELECTABLE_YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR + i);
const MONTHS = Array.from({ length: 12 }, (_, i) => String(i + 1));
export const MONTH_LABELS = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];

const emptySeries = () => ({ days: {}, fallback_months: [] });

// 指定月の値が全て一致するか比較する。JSON.stringify によるキー順依存の
// 誤判定を避けるため、MONTHS を基準に1件ずつ突き合わせる。
const daysEqual = (a, b) => MONTHS.every((month) => a[month] === b[month]);

const WorkingDaysMasterModal = ({ open, onClose }) => {
	const api = useAxios();
	// 保存成功をヘッダーの WorkingDaysUnsetAlert へ伝える（未設定月を解消しても
	// ページ再読み込みまで赤字アラートが残り続けることを防ぐ）
	const { notifyWorkingDaysSaved } = useContext(AdminModalContext);
	const [year, setYear] = useState(CURRENT_YEAR);
	const [japan, setJapan] = useState(emptySeries());
	const [cairo, setCairo] = useState(emptySeries());
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	// サーバーから取得した直近の値。編集済みかどうか(isDirty)の比較基準にする。
	const snapshotRef = useRef({ japan: emptySeries(), cairo: emptySeries() });
	// リクエストの一意な識別に使うグローバル連番。
	const requestIdRef = useRef(0);
	// 年度ごとの最後発リクエストIDを覚えておく。年度切替の連打や、保存完了後に
	// handleSave が発行する再取得(旧年度宛)が新しい年度の取得より後発になっても、
	// 「その年度自身にとって最後発の応答か」を年度単位で判定するために使う
	// (Issue #71 / PR #100 Codexレビュー: グローバル連番だけで判定すると、保存中に
	// 年度を切り替えた際、保存完了後に届く旧年度の再取得応答が「グローバルには最後発」
	// になってしまい、現在選択中の年度の表示を旧年度の値で汚染してしまう)。
	const latestRequestIdByYearRef = useRef(new Map());
	// 現在選択中の年度を非同期コールバック内から常に最新の値で参照するためのref。
	const yearRef = useRef(year);
	useEffect(() => {
		yearRef.current = year;
	}, [year]);

	const fetchYear = useCallback(
		async (targetYear) => {
			const requestId = ++requestIdRef.current;
			latestRequestIdByYearRef.current.set(targetYear, requestId);
			// 対象外年度の再取得(例: 保存中に年度を切り替えた後、保存完了時に
			// handleSaveが発行する旧年度の再取得)では、グローバルなloadingを
			// 立てない。ここで無条件にtrueにすると、既に新年度の取得が完了して
			// 読み込み中表示が解除された後にこの呼び出しが走った場合、finally側は
			// targetYear !== yearRef.currentのため解除できず、選択中の年度の
			// 表示が読み込み中のまま固まってしまう(Issue #71 / PR #100 Codexレビュー)。
			if (targetYear === yearRef.current) {
				setLoading(true);
			}
			try {
				const response = await api.get("working-days-master/get_year/", {
					params: { year: targetYear },
				});
				const isLatestForThisYear =
					latestRequestIdByYearRef.current.get(targetYear) === requestId;
				const isCurrentlySelectedYear = targetYear === yearRef.current;
				if (!isLatestForThisYear || !isCurrentlySelectedYear) return;
				setJapan(response.data.japan);
				setCairo(response.data.cairo);
				snapshotRef.current = {
					japan: response.data.japan,
					cairo: response.data.cairo,
				};
			} catch (error) {
				const isLatestForThisYear =
					latestRequestIdByYearRef.current.get(targetYear) === requestId;
				const isCurrentlySelectedYear = targetYear === yearRef.current;
				if (!isLatestForThisYear || !isCurrentlySelectedYear) return;
				message.error("Failed to load working days");
			} finally {
				const isLatestForThisYear =
					latestRequestIdByYearRef.current.get(targetYear) === requestId;
				const isCurrentlySelectedYear = targetYear === yearRef.current;
				if (isLatestForThisYear && isCurrentlySelectedYear) setLoading(false);
			}
		},
		[api],
	);

	useEffect(() => {
		if (open) {
			fetchYear(year);
		}
		// open/year の変化時のみ再取得する（api は毎レンダー新インスタンスのため依存に含めない）
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, year]);

	const isDirty =
		!daysEqual(japan.days, snapshotRef.current.japan.days) ||
		!daysEqual(cairo.days, snapshotRef.current.cairo.days);

	// 未保存編集があるときだけ破棄確認を挟む。確認不要/OK確定時に onConfirmed を実行する。
	// PR #100 Codexレビュー対応: 保存API送信中はクローズ/年度切替の操作自体を
	// 無効化するため(下のhandleRequestClose/handleYearChangeガード・Select
	// disabled・cancelButtonProps参照)、保存中にこの確認が出ることはない。
	// 「破棄する」とOKしたのに送信済みbulk_updateで保存される齟齬、および
	// 保存失敗時に編集値だけ失われる齟齬(2巡目指摘)の両方を操作遮断で防ぐ。
	const confirmDiscardIfDirty = (onConfirmed) => {
		if (!isDirty) {
			onConfirmed();
			return;
		}
		Modal.confirm({
			title: "Discard unsaved changes?",
			content: "Your edits to working days have not been saved. Discard them?",
			onOk: onConfirmed,
		});
	};

	const handleRequestClose = () => {
		// PR #100 Codexレビュー対応: 保存API送信中はキャンセル/破棄確認を受け付けない。
		// 送信済みのbulk_updateは止められないため、「破棄したのに保存される」
		// 齟齬を防ぐ(下のcancelButtonProps/maskClosable/closableも同趣旨)。
		if (saving) return;
		confirmDiscardIfDirty(() => {
			setJapan(snapshotRef.current.japan);
			setCairo(snapshotRef.current.cairo);
			onClose();
		});
	};

	const handleYearChange = (newYear) => {
		// PR #100 Codexレビュー対応(2巡目): 保存中の年度切替は受け付けない。
		// 切替を許すと編集値がスナップショットへ戻され、保存が失敗した場合に
		// 「保存されていないのに編集値だけ失われた」状態になるため。
		if (saving) return;
		confirmDiscardIfDirty(() => {
			// 破棄確定時は先に編集値をスナップショットへ戻す。新年度のget_year
			// が返るまでの間、旧年度で編集中だった値が画面上に残り続けるのを防ぐ
			// (Codexレビュー3回目対応)。Saveボタン自体もloading中は下でdisableする
			// ため、この間にbulk_updateへ送信されることはないが、表示の整合性
			// (取得完了前に旧年度の値が見えてしまう状態)もあわせて解消する。
			setJapan(snapshotRef.current.japan);
			setCairo(snapshotRef.current.cairo);
			setYear(newYear);
		});
	};

	const handleSave = async () => {
		setSaving(true);
		try {
			const response = await api.post("working-days-master/bulk_update/", {
				year,
				japan: japan.days,
				cairo: cairo.days,
			});
			message.success(`Working days saved (${response.data.updated} changed)`);
			notifyWorkingDaysSaved();
			await fetchYear(year);
		} catch (error) {
			// Issue #80: DRF のエラーを JSON.stringify した生の文字列で出していたため、
			// 利用者には読めず内部構造もそのまま見えていた。読める一文へ整形する。
			message.error(
				formatApiErrorDetail(
					error.response?.data,
					"Failed to save working days. Please try again.",
				),
			);
		} finally {
			setSaving(false);
		}
	};

	const seriesRows = [
		{ key: "japan", label: "Japan", series: japan, setSeries: setJapan },
		{ key: "cairo", label: "Cairo", series: cairo, setSeries: setCairo },
	];

	const columns = [
		{ title: "", dataIndex: "label", key: "label", width: 70 },
		...MONTHS.map((month) => ({
			title: MONTH_LABELS[Number(month) - 1],
			key: month,
			width: 70,
			render: (_, row) => (
				<InputNumber
					min={0}
					max={31}
					size="small"
					style={{ width: "100%" }}
					value={row.series.days[month]}
					// 未設定月（フォールバック値表示中）は枠色で区別する
					status={
						row.series.fallback_months.includes(Number(month)) ? "warning" : ""
					}
					onChange={(value) =>
						row.setSeries((prev) => ({
							...prev,
							days: { ...prev.days, [month]: value },
						}))
					}
				/>
			),
		})),
	];

	return (
		<Modal
			title="Working Days Master"
			open={open}
			onOk={handleSave}
			okText="Save"
			confirmLoading={saving}
			// 対象年度のデータ取得が完了するまでSaveを押せなくする。取得中は
			// japan/cairoがまだ選択中の年度に対応していない可能性があるため、
			// ここでbulk_updateへ送信できてしまうと別年度の値で上書きしてしまう
			// (Codexレビュー3回目対応: PR #100 / Issue #71)。
			okButtonProps={{ disabled: loading }}
			cancelButtonProps={{ disabled: saving }}
			closable={!saving}
			maskClosable={!saving}
			onCancel={handleRequestClose}
			width="95%"
			style={{ maxWidth: 1100 }}
		>
			<Alert
				message="Set the monthly working days (TCD) for Japan / Cairo by year. Saved values apply to reports and utilization calculations from the current month onward."
				type="info"
				style={{ marginBottom: 12 }}
			/>
			<div style={{ marginBottom: 12 }}>
				<span style={{ marginRight: 8 }}>Year:</span>
				<Select
					value={year}
					onChange={handleYearChange}
					options={SELECTABLE_YEARS.map((y) => ({ value: y, label: y }))}
					style={{ width: 100 }}
					disabled={saving}
				/>
			</div>
			<Table
				columns={columns}
				dataSource={seriesRows}
				rowKey="key"
				pagination={false}
				loading={loading}
				tableLayout="fixed"
				size="small"
				// 列合計は 910px (ラベル70 + 月×12×70)。モーダル幅がこれより広い
				// (通常のデスクトップ表示)ではスクロールバーは出ず、モーダルが
				// 画面幅に合わせて縮む狭い画面でのみテーブル内部だけがスクロール
				// する(ページ全体が横スクロールする状態を防ぐ)。
				scroll={{ x: 910 }}
			/>
		</Modal>
	);
};

export default WorkingDaysMasterModal;
